import { useFormContext } from 'react-hook-form';
import { Box, Flex, Text } from '@chakra-ui/react';
import { Checkbox, OutlineCard, Select } from '@morressier/alembic';
import { FieldLabel } from 'src/components/Field';
import { IGetSubmissionTransferNetworkResponse } from '@publishing-engine/submission-service/src/endpoints/api.response-types';
import { FormFieldTargetJournals as IFormFieldCheckbox } from '@publishing-engine/domain/types/article';
import FormFieldErrorMessage from 'src/components/FormField/common/FormFieldErrorMessage';
import { journalPublishingModels } from '@publishing-engine/domain/models';
import { useState, useEffect } from 'react';

type TargetJournalEntry = {
  id: string;
  articleTypeCategoryId: string;
};

type Props = {
  config: IFormFieldCheckbox;
  options: IGetSubmissionTransferNetworkResponse | undefined;
};

export default function SelectArticleTypes({
  config,
  options
}: Props) {
  const {
    watch,
    setValue,
    clearErrors
  } = useFormContext();

  const {
    params: { validations, labels },
    caseDataFields: { main: field }
  } = config;

  // Watch targetJournals from form
  const targetJournals = (watch(field) as TargetJournalEntry[]) || [];

  // Local state to track selections before they're complete
  const [localSelections, setLocalSelections] = useState<Record<string, string>>({});

  const selectOptions = options?.map(
    ({ id, journalName, publishingModel, articleTypes }) => ({
      id,
      main: journalName,
      secondary: `${journalPublishingModels[publishingModel]} Journal`,
      articleTypes
    })
  );

  // Initialize local selections from form data on mount
  useEffect(() => {
    if (targetJournals.length > 0 && Object.keys(localSelections).length === 0) {
      const initialSelections: Record<string, string> = {};
      targetJournals.forEach((tj: TargetJournalEntry) => {
        initialSelections[tj.id] = tj.articleTypeCategoryId;
      });
      setLocalSelections(initialSelections);
    }
  }, [targetJournals]);

  // Helper to get articleTypeCategoryId for a specific journal
  const getArticleTypeCategoryId = (journalId: string): string => {
    return localSelections[journalId] || '';
  };

  // Update form value with complete entries only
  const updateFormValue = (selections: Record<string, string>, shouldValidate: boolean = false) => {
    const completeEntries: TargetJournalEntry[] = Object.entries(selections)
      .filter(([, articleTypeId]) => articleTypeId && articleTypeId.length === 26)
      .map(([journalId, articleTypeId]) => ({
        id: journalId,
        articleTypeCategoryId: articleTypeId
      }));

    setValue(field, completeEntries, {
      shouldValidate,
      shouldDirty: true,
      shouldTouch: true
    });
  };

  // Handle checkbox change
  const handleCheckboxChange = (
    checked: boolean,
    journalId: string
  ) => {
    clearErrors(field);

    if (checked) {
      // Add to local selections with empty article type (user needs to select)
      const newSelections = { ...localSelections, [journalId]: '' };
      setLocalSelections(newSelections);
      // Don't update form yet - wait for article type selection
      // This prevents validation error for incomplete entries
    } else {
      // Remove from local selections and form
      const newSelections = { ...localSelections };
      delete newSelections[journalId];
      setLocalSelections(newSelections);
      // Update form without triggering validation (user is removing, not submitting)
      updateFormValue(newSelections, false);
    }
  };

  // Handle article type change for a specific journal
  const handleArticleTypeChange = (
    journalId: string,
    articleTypeCategoryId: string
  ) => {
    const newSelections = { ...localSelections, [journalId]: articleTypeCategoryId };
    setLocalSelections(newSelections);
    
    // Only update form and validate if we have a valid article type (26 chars = valid ID)
    if (articleTypeCategoryId && articleTypeCategoryId.length === 26) {
      clearErrors(field);
      updateFormValue(newSelections, true);
    } else {
      // If article type is cleared, update form without validation
      updateFormValue(newSelections, false);
    }
  };

  // Count selected journals for display (from local selections)
  const selectedCount = Object.keys(localSelections).length;

  return (
    <OutlineCard borderless padding={6} width="100%">
      <Flex flexDirection="column" padding={3} paddingTop={2} gap={3}>
        <Flex flexDirection="column" alignItems="flex-start" gap={4}>
          {selectOptions?.map((item) => (
            <Flex
              key={item.id}
              gap={4}
              alignItems={'center'}
              flexDirection={'row'}
              width={'full'}
              borderBottom={'1px'}
              borderColor="neutralPrimaryBorder"
              paddingY={2}
            >
              <Box flex={1} my={2}>
                <FieldLabel
                  isRequired={validations.required}
                  label={labels.subtitle}
                  subText={`${selectedCount}/${options?.length} ${labels.main} Selected`}
                  helpText={labels.helpText}
                />
                <Checkbox
                  my={4}
                  ariaLabel={item.main}
                  id={`${field}-${item.id}`}
                  name={field}
                  isChecked={item.id in localSelections}
                  onChange={({ target: { checked } }) =>
                    handleCheckboxChange(checked, item.id)
                  }
                >
                  <Flex flexDirection="column">
                    <Text textStyle={'textBody'}>{item.main}</Text>
                    {item.secondary && (
                      <Text textStyle={'textBody'}>{item.secondary}</Text>
                    )}
                  </Flex>
                </Checkbox>
              </Box>
              <Box flex={1}>
                <FieldLabel
                  isRequired={validations.required}
                  label={'Article Type'}
                  subText="Select article type"
                />
                <Select
                  my={4}
                  value={getArticleTypeCategoryId(item.id)}
                  onChange={(e) =>
                    handleArticleTypeChange(item.id, e.target.value)
                  }
                  disabled={!(item.id in localSelections)}
                >
                  <option value="">Select an option</option>
                  {item.articleTypes?.map((option) => (
                    <option
                      key={option.articleTypeCategoryId}
                      value={option.articleTypeCategoryId}
                    >
                      {option.name}
                    </option>
                  ))}
                </Select>
              </Box>
            </Flex>
          ))}
        </Flex>

        <FormFieldErrorMessage config={config} />
      </Flex>
    </OutlineCard>
  );
}
